import {
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import React from 'react';

export interface Genre {
  id: number;
  name: string;
  sub_genres?: Genre[];
}

interface Props {
  uiSearchType: string;
  genresData: Genre[];
  isFetched: boolean;
  isError: boolean;
  speciality: Genre[];
  sponserCommercial: Genre[];
  onGenreChange: (e: SelectChangeEvent<string>) => void;
  setSpecialityFilterId: (id: number) => void;
  setCustomFilterId: (ids: number[]) => void;
}

const TrackFilterSelect: React.FC<Props> = ({
  uiSearchType,
  genresData,
  isFetched,
  isError,
  speciality,
  sponserCommercial,
  onGenreChange,
  setSpecialityFilterId,
  setCustomFilterId,
}) => {
  const renderSelectBox = (
    label: string,
    id: string,
    defaultOptionLabel: string = 'Genres',
    options: JSX.Element[] = [],
    onChange: (e: SelectChangeEvent<string>) => void = () => {
      // no-op
    },
  ) => (
    <>
      <InputLabel
        htmlFor={id}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            backgroundColor: 'transparent !important',
          },
        }}>
        {label}
      </InputLabel>
      <Select
        native
        defaultValue='0'
        id={id}
        label={label}
        onChange={onChange}
        SelectDisplayProps={{'aria-controls': undefined}}>
        <option value='0'>{defaultOptionLabel}</option>
        {options}
      </Select>
    </>
  );

  const genreOptions =
    isFetched && !isError
      ? genresData.map(value => (
          <optgroup label={value.name} key={value.id}>
            {value.sub_genres?.map(sub => (
              <option value={sub.id} key={sub.id}>
                {sub.name}
              </option>
            ))}
          </optgroup>
        ))
      : [];

  const specialityOptions = speciality.map(item => (
    <option key={item.id} value={item.id}>
      {item.name}
    </option>
  ));

  const excludedNames = ['Sponsor Commercials', 'Custom Clips'];
  const customOptions = sponserCommercial
    .filter(({name}) => !excludedNames.includes(name))
    .map(({id, name}) => (
      <option key={id} value={id}>
        {name}
      </option>
    ));

  const getAllValidCustomIds = (data: {id: number; name: string}[]) => {
    return data.filter(item => item.id >= 0).map(item => item.id);
  };

  let selectBox: JSX.Element | null = null;
  switch (uiSearchType) {
    case 'music-library':
      selectBox = renderSelectBox(
        'Genres',
        'grouped-native-select',
        'ALL',
        genreOptions,
        onGenreChange,
      );
      break;

    case 'speciality-category':
      selectBox = renderSelectBox(
        'Specialty',
        'specialty-select',
        'ALL',
        specialityOptions,
        e => setSpecialityFilterId(Number(e.target.value)),
      );
      break;

    case 'custom-library':
      selectBox = renderSelectBox(
        'Single Play Library',
        'custom-library-select',
        'ALL',
        customOptions,
        e => {
          const value = e.target.value;
          setCustomFilterId(
            value === '0'
              ? getAllValidCustomIds(sponserCommercial)
              : [Number(value)],
          );
        },
      );
      break;

    default:
      selectBox = renderSelectBox('Genres', 'grouped-native-select');
      break;
  }

  return (
    <FormControl fullWidth className='ml-2'>
      {selectBox}
    </FormControl>
  );
};

export default TrackFilterSelect;
